import React, { useCallback } from "react";

import { useReviews } from "~/hooks/useReviews";

import AddNewQuestion from "./AddNewQuestion";
import QuestionsItem from "./QuestionsItem";
import ReviewsScoreList from "./ReviewsScoreList";
import ReviewsScoreSummary from "./ReviewsScoreSummary";

import {
  ReviewCard,
  FormRangeSlider,
  TabbedSection,
  FormSelect,
  Button,
} from "@bared/ui";

type ReviewsProps = {
  gender: "Mens" | "Womens" | null;
  productGroup: string;
  productHandle: string;
  productName: string;
  sku: string;
  siblingColours?: {
    siblingColour: string;
    siblingHandle: string;
    image: string;
  }[];
  onSheetToggle: () => void;
  sharedReviewFilter: number;
  onReviewFilterChange: (filter: number) => void;
};

export const Reviews = ({
  siblingColours,
  gender,
  productGroup,
  productHandle,
  productName,
  sku,
  onSheetToggle,
  sharedReviewFilter,
  onReviewFilterChange,
}: ReviewsProps) => {
  const {
    averageScore,
    reviews,
    questions,
    starDistribution,
    totalQuestions,
    totalReviews,
    fitFeedbackStatistic,
    questionsSortOptions,
    questionsPerPage,
    loading,
    reviewFilter,
    tabs,
    handleClearReviewFilter,
    handleClickReviewFilter,
    handleQuestionsSortOptionChange,
    handleTabChange,
  } = useReviews({
    productGroup,
    sku,
    reviewSize: 3,
    isReviewsSheetOpen: true,
    initialReviewFilter: sharedReviewFilter,
  });

  // Wrap filter handlers to update shared state
  const handleReviewFilterClick = useCallback(
    (filter: number) => {
      handleClickReviewFilter(filter);
      onReviewFilterChange(filter);
    },
    [handleClickReviewFilter, onReviewFilterChange],
  );

  const handleReviewFilterClear = useCallback(() => {
    handleClearReviewFilter();
    onReviewFilterChange(0);
  }, [handleClearReviewFilter, onReviewFilterChange]);

  return (
    <section className="relative py-maxi">
      {loading && <p className="text-body leading-hint">Loading...</p>}

      <TabbedSection tabs={tabs} onChange={() => handleTabChange()}>
        {(tabhandle) => {
          if ("reviews" === tabhandle) {
            return totalReviews > 0 ? (
              <div>
                <div className="grid grid-cols-1 md:grid-cols-6">
                  <div className="col-span-2 flex flex-col space-y-maxi space-y-mini py-midi md:min-w-[80%] md:max-w-[80%]">
                    <ReviewsScoreSummary
                      averageScore={averageScore}
                      productGroup={productGroup}
                    />

                    <ReviewsScoreList
                      className="[&_.review-bar]:md:w-full"
                      reviewFilter={reviewFilter}
                      totalReviews={totalReviews}
                      starDistribution={starDistribution}
                      handleClick={(filter: number) => {
                        handleReviewFilterClick(filter);
                        onSheetToggle();
                      }}
                      handleClear={handleReviewFilterClear}
                    />
                    {gender === "Womens" || gender === "Mens" ? (
                      <div className="flex w-full flex-col space-y-maxi">
                        {fitFeedbackStatistic.map((stats) => {
                          return (
                            <div className="flex flex-col space-y-mini">
                              <p className="text-caption font-medium leading-caption">
                                {stats.title}:
                              </p>
                              <FormRangeSlider
                                rangeOption={stats.rangeOptions}
                                value={stats.averageRating}
                                min={1}
                                max={5}
                                step={0.01}
                              />
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>

                  <div
                    data-section-id="reviews-container"
                    className="col-span-4"
                  >
                    <div>
                      <div className="relative">
                        {loading ? (
                          <p className="pt-midi text-body leading-hint">
                            Loading ...
                          </p>
                        ) : (
                          reviews.map(
                            (
                              {
                                id,
                                rating,
                                title,
                                comments,
                                author: { name, location },
                                productName,
                                timeAgo,
                                replies,
                              },
                              index,
                            ) => {
                              const shoeSize =
                                productName.split(" / ")[1] || undefined;

                              const styleName = siblingColours
                                ?.filter(({ siblingColour }) => {
                                  const regex = /-/g;
                                  return !siblingColour.match(regex);
                                })
                                .find(({ siblingColour }) =>
                                  productName.includes(siblingColour),
                                );

                              const emptyLocation = location.replace('""', "");
                              const country =
                                location.split(",").length > 1
                                  ? location.split(",")[1]
                                  : location;

                              return (
                                <ReviewCard
                                  key={id}
                                  userName={name}
                                  country={
                                    !!emptyLocation ? country : undefined
                                  }
                                  shoeHandle={styleName?.siblingHandle}
                                  shoeImage={styleName?.image}
                                  shoeStyle={styleName?.siblingColour}
                                  shoeSize={shoeSize}
                                  rating={rating}
                                  timeAgo={timeAgo}
                                  reviewTitle={title}
                                  review={comments}
                                  response={replies?.[0]?.comments}
                                  isFirst={index === 0}
                                />
                              );
                            },
                          )
                        )}

                        {!loading &&
                        totalReviews !== 0 &&
                        reviews.length === 0 ? (
                          <p className="pt-midi text-body leading-hint">{`No ${reviewFilter ? `${reviewFilter}-star ` : ""}reviews found. `}</p>
                        ) : null}

                        {totalReviews > 3 && reviews.length !== 0 && (
                          <div
                            className="right-0 bottom-0 left-0 pointer-events-none absolute h-[85px]"
                            style={{
                              background:
                                "linear-gradient(180deg, rgba(247, 247, 247, 0.00) 1.89%, #F7F7F7 58.23%)",
                            }}
                          />
                        )}
                      </div>

                      {totalReviews > 3 && reviews.length !== 0 && (
                        <Button
                          variant="primary"
                          size="large"
                          onClick={onSheetToggle}
                          className="w-full"
                        >
                          Read All Reviews
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="pt-mini text-body leading-hint">
                There are currently no reviews.
              </p>
            );
          }

          if ("questions" === tabhandle) {
            return totalQuestions > 0 ? (
              <div className="pt-mini">
                <div className="flex w-full flex-col items-start justify-between space-y-midi md:flex-row md:items-center md:space-y-zero">
                  <AddNewQuestion
                    sku={sku}
                    productHandle={productHandle}
                    productName={productName}
                  />

                  <div className="flex w-full items-center space-x-mini">
                    <div className="w-1/2 md:max-w-[215px]">
                      <FormSelect
                        className="border text-caption"
                        placeholder=""
                        defaultValue={questionsSortOptions[0].value}
                        options={questionsSortOptions}
                        onChange={handleQuestionsSortOptionChange}
                      />
                    </div>

                    <p className="text-caption leading-caption">
                      {questionsPerPage} of {totalQuestions} Questions
                    </p>
                  </div>
                </div>

                <div data-section-id="questions-container">
                  <div>
                    {loading ? (
                      <p className="text-body leading-hint">Loading ...</p>
                    ) : (
                      questions.map(
                        ({
                          id,
                          author: { name },
                          comments,
                          timeAgo,
                          replies,
                        }) => {
                          return (
                            <QuestionsItem
                              className="last:mb-4 border-b-[1px] border-b-primary-300 py-midi"
                              key={id}
                              userName={name}
                              question={comments}
                              timeAgo={timeAgo}
                              responses={replies}
                            />
                          );
                        },
                      )
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex w-full flex-col items-start justify-between space-y-micro pt-mini md:flex-row md:items-center md:space-y-zero">
                <p className="text-body">Ask us anything.</p>
                <AddNewQuestion
                  sku={sku}
                  productHandle={productHandle}
                  productName={productName}
                />
              </div>
            );
          }
        }}
      </TabbedSection>
    </section>
  );
};
